import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import type { DefiMfa, User } from './AuthContext'
import { BOUTON_PRINCIPAL, CARTE_COMPTE, CHAMP, FOND_COMPTE, LIBELLE, LIEN } from './charte/compte'

const API = (import.meta.env.VITE_API_URL || '') + '/api/auth/mfa'
export const ERREUR_MFA = 'Code ou demande invalide. Patientez puis réessayez, ou recommencez la connexion.'
export interface SessionMfa extends Omit<User, 'email'> { token: string; codes_secours?: string[] }

export function CodesSecours({ codes, onConserves }: { codes: string[]; onConserves: () => void }) {
  return <div className="space-y-4">
    <h2 className="text-xl font-semibold">Conservez vos codes de secours</h2>
    <p>Ces codes ne seront affichés qu’une seule fois. Conservez-les dans un endroit sûr,
      accessible même si vous perdez Proton Pass. Chaque code ne sert qu’une fois.</p>
    <ul className="space-y-1 font-mono text-sm select-all" aria-label="Codes de secours">
      {codes.map(code => <li key={code}>{code}</li>)}
    </ul>
    <button className={BOUTON_PRINCIPAL} onClick={onConserves}>J’ai conservé mes codes</button>
  </div>
}

/** Page d'enrôlement affichée dans /login : aucun secret dans l'URL ou le stockage. */
export default function MfaConnexion({ defi, onSession, onRecommencer }: {
  defi: DefiMfa; onSession: (session: SessionMfa) => void; onRecommencer: () => void
}) {
  const enrolement = defi.enrolement_requis === true
  const [parametres, setParametres] = useState<{ secret: string; uri: string } | null>(null)
  const [secours, setSecours] = useState(false)
  const [code, setCode] = useState('')
  const [erreur, setErreur] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [session, setSession] = useState<SessionMfa | null>(null)
  const presente = useRef(true)
  useEffect(() => {
    presente.current = true
    return () => { presente.current = false }
  }, [])

  useEffect(() => {
    if (!enrolement) return
    let present = true
    axios.post(`${API}/enrolement`, { defi: defi.defi })
      .then(r => { if (present) setParametres(r.data) })
      .catch(() => { if (present) setErreur('Cette demande a expiré ou est indisponible. Recommencez la connexion.') })
    return () => { present = false }
  }, [defi.defi, enrolement])

  const valider = async (e: React.FormEvent) => {
    e.preventDefault()
    setEnCours(true); setErreur('')
    try {
      const r = await axios.post<SessionMfa>(`${API}/${enrolement ? 'confirmer' : 'verifier'}`, { defi: defi.defi, code })
      // « Recommencer » ou quitter la page abandonne aussi la réponse encore en vol.
      if (!presente.current) return
      setCode(''); setParametres(null)
      if (enrolement) setSession(r.data)
      else onSession(r.data)
    } catch { if (presente.current) setErreur(ERREUR_MFA) }
    finally { if (presente.current) setEnCours(false) }
  }

  return <div className={FOND_COMPTE}>
    <div className={`w-full max-w-lg ${CARTE_COMPTE} space-y-5`}>
      {session?.codes_secours ? <CodesSecours codes={session.codes_secours} onConserves={() => onSession(session)} /> : <>
        <h1 className="text-xl font-semibold">{enrolement ? 'Activez la double authentification' : 'Vérifiez votre connexion'}</h1>
        {enrolement ? <>
          <p>La double authentification est obligatoire pour votre compte. Dans Proton Pass,
            ajoutez un code de vérification à votre identifiant TerObs avec cette clé.</p>
          {parametres ? <>
            <p className="font-mono break-all select-all" aria-label="Clé à recopier">{parametres.secret}</p>
            <label className={LIBELLE} htmlFor="mfa-uri">Adresse à importer dans Proton Pass</label>
            <textarea id="mfa-uri" readOnly value={parametres.uri} className={CHAMP} />
            <p>Recopiez ensuite le code à 6 chiffres affiché par Proton Pass.</p>
          </> : !erreur && <p role="status">Préparation de votre clé…</p>}
        </> : <p>{secours ? 'Saisissez un de vos codes de secours. Il ne pourra plus être utilisé ensuite.'
          : 'Saisissez le code affiché par Proton Pass.'}</p>}
        <form onSubmit={valider} className="space-y-4">
          <label className={LIBELLE} htmlFor="mfa-code">{secours ? 'Code de secours' : 'Code à 6 chiffres'}</label>
          <input id="mfa-code" className={CHAMP} value={code} onChange={e => setCode(e.target.value)}
            autoComplete="one-time-code" inputMode={secours ? 'text' : 'numeric'}
            pattern={secours ? undefined : '[0-9]{6}'} maxLength={secours ? 128 : 6} required autoFocus />
          {erreur && <p role="alert" className="text-red-700">{erreur}</p>}
          <button className={BOUTON_PRINCIPAL} disabled={enCours || (enrolement && !parametres)}>
            {enCours ? 'Vérification…' : enrolement ? 'Activer la double authentification' : 'Valider le code'}
          </button>
        </form>
        {!enrolement && <button className={LIEN} onClick={() => { setSecours(!secours); setCode(''); setErreur('') }}>
          {secours ? 'Utiliser le code de Proton Pass' : 'Utiliser un code de secours'}
        </button>}
        <button className={`${LIEN} block`} onClick={onRecommencer}>Recommencer la connexion</button>
      </>}
    </div>
  </div>
}
